import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { DollarSign, TrendingUp, Clock, AlertCircle, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

const BUCKETS = [
  { key: "current", label: "Current", color: "emerald" },
  { key: "1-30", label: "1–30 days", color: "blue" },
  { key: "31-60", label: "31–60 days", color: "amber" },
  { key: "61-90", label: "61–90 days", color: "orange" },
  { key: "90+", label: "90+ days", color: "red" },
];
const COLOR = {
  emerald: "border-emerald-500/30 text-emerald-300 bg-emerald-500/5",
  blue: "border-blue-500/30 text-blue-300 bg-blue-500/5",
  amber: "border-amber-500/30 text-amber-300 bg-amber-500/5",
  orange: "border-orange-500/30 text-orange-300 bg-orange-500/5",
  red: "border-red-500/30 text-red-300 bg-red-500/5",
};

export default function CashFlow() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/ai-ops/cash-flow").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-slate-500">Loading…</div>;
  const max = Math.max(...Object.values(data.buckets).map((b) => b.amount), 1);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="cash-flow-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-600/15 border border-blue-500/20 flex items-center justify-center">
          <DollarSign className="w-6 h-6 text-blue-400" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">Financial</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Cash Flow & Aging</h1>
          <p className="text-sm text-slate-400 mt-1">Outstanding invoices grouped by age · expected inflow next 30 days.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <SummaryCard label="Total Outstanding" value={`$${data.total_outstanding.toLocaleString()}`} icon={DollarSign} sub={`${Object.values(data.buckets).reduce((s, b) => s + b.count, 0)} invoices`} />
        <SummaryCard label="Expected 30-day Inflow" value={`$${data.expected_30d_inflow.toLocaleString()}`} icon={TrendingUp} sub={`Based on ${data.paid_rate}% historical paid rate`} accent="emerald" />
        <SummaryCard label="Aged > 60 days" value={`$${((data.buckets["61-90"]?.amount || 0) + (data.buckets["90+"]?.amount || 0)).toLocaleString()}`} icon={AlertCircle} sub="At risk — call carrier" accent="red" />
      </div>

      <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-4">Aging Buckets</div>
        <div className="space-y-3">
          {BUCKETS.map((b) => {
            const bucket = data.buckets[b.key];
            const pct = (bucket.amount / max) * 100;
            return (
              <div key={b.key} data-testid={`bucket-${b.key}`} className={`rounded-lg border p-3 ${COLOR[b.color]}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider font-bold">{b.label}</div>
                  <div className="flex items-center gap-3">
                    <div className="text-[10px] text-slate-500">{bucket.count} invoices</div>
                    <div className="font-mono font-bold text-base">${bucket.amount.toLocaleString()}</div>
                  </div>
                </div>
                <div className="w-full h-1.5 rounded-full bg-slate-900/60 overflow-hidden">
                  <div className={`h-full bg-current`} style={{ width: `${pct}%`, opacity: 0.5 }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {data.top_owing_carriers.length > 0 && (
        <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Top Carriers Owing</div>
          <div className="space-y-2">
            {data.top_owing_carriers.map((c, idx) => (
              <div key={idx} className="flex items-center justify-between rounded-lg bg-slate-900/40 border border-slate-800 p-3">
                <div className="flex items-center gap-3">
                  <div className="text-xs uppercase tracking-wide font-bold text-slate-500 w-6">#{idx + 1}</div>
                  <div className="text-sm font-medium">{c.carrier}</div>
                </div>
                <div className="font-mono text-blue-300 font-semibold">${c.amount.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const SummaryCard = ({ label, value, icon: Icon, sub, accent = "blue" }) => {
  const acc = { blue: "text-blue-400 bg-blue-500/10", emerald: "text-emerald-400 bg-emerald-500/10", red: "text-red-400 bg-red-500/10" }[accent];
  return (
    <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</div>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${acc}`}><Icon className="w-4 h-4" /></div>
      </div>
      <div className="font-display text-3xl font-semibold tracking-tight">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1.5">{sub}</div>}
    </div>
  );
};
